import { activeSettingsGroup, settingsGroups } from "./settings-nav";
import type { OperatingModule } from "../api/operatingMode";
import {
  Activity,
  Banknote,
  Calendar,
  CalendarPlus,
  Dna,
  Globe,
  LayoutDashboard,
  Microscope,
  Package,
  Percent,
  Pill,
  ClipboardList,
  Receipt,
  SlidersHorizontal,
  Smartphone,
  Star,
  Stethoscope,
  Syringe,
  Ticket,
  Timer,
  TrendingUp,
  MessageSquare,
  Users,
  type LucideIcon,
} from "lucide-react";

export type NavItem = {
  label: string;
  path: string;
  module: string;
  icon: LucideIcon;
  /** For settings-category items: highlight active whenever the current route
   *  falls inside this settings category (not just an exact path match). */
  settingsGroup?: string;
  /** Hide this item when the given clinic feature flag is off (ADR-0027). */
  requiresFeature?: "prescribeEnabled" | "manageInventory";
  /**
   * Hide this item unless the signed-in user holds this permission (#18746).
   *
   * Presentation only — the endpoint behind the page enforces the same permission. Hiding a
   * menu nobody may use spares the clinic a dead end; it is not what keeps them out.
   *
   * A list means ANY one of them is enough (#19860: "Tổng quan vận hành" opens for readers of
   * clinic-wide figures and for doctors holding only "số của tôi").
   */
  requiresPermission?: string | readonly string[];
  /**
   * Hide this item when the clinic does not operate the module in Addy (ADR-0048).
   *
   * Unlike `requiresFeature`, which only ever hid a menu, the endpoint behind the page answers
   * 409 for the same clinic. Hiding it spares a dead end; the 409 is what stops the record being
   * written by someone who typed the URL.
   */
  requiresModule?: OperatingModule;
};

export type NavGroup = {
  label: string;
  items: NavItem[];
  /** Collapsed by default in the sidebar (settings groups start folded). */
  defaultCollapsed?: boolean;
};

export const navGroups: NavGroup[] = [
  {
    label: "Hoạt động",
    items: [
      { label: "Tổng quan", path: "/dashboard", module: "dashboard", icon: LayoutDashboard },
      { label: "Lịch hẹn", path: "/appointments", module: "appointments", icon: Calendar },
      // #18584 P5: "Lịch trong ngày" (/agenda) hidden from the sidebar to kill the near-duplicate
      // name clash with "Lịch ngày" (/appointments/day, the Kanban tab inside Lịch hẹn). The route
      // + page stay live (reachable by URL / deep links) — "hide but keep working". To swap which
      // one is hidden, restore this entry and drop the /appointments/day tab instead.
      { label: "Tiếp đón & Hàng chờ", path: "/reception", module: "appointments", icon: Ticket },
      { label: "Khám bệnh", path: "/encounters", module: "encounters", icon: Stethoscope, requiresModule: "ENCOUNTERS" },
      { label: "Bệnh nhân", path: "/patients", module: "patients", icon: Users },
      // #18746 — opens the clinic's messaging console on Addy Message. Behind its own
      // permission because whoever holds it can read every message sent to every patient.
      { label: "Tin nhắn", path: "/messaging", module: "messaging", icon: MessageSquare, requiresPermission: "messaging.console" },
    ],
  },
  {
    label: "Chuyên môn",
    items: [
      { label: "Đơn thuốc", path: "/prescriptions", module: "prescriptions", icon: Pill, requiresFeature: "prescribeEnabled", requiresModule: "PRESCRIPTIONS" },
      { label: "Tồn kho thuốc", path: "/inventory/stock", module: "inventory", icon: Package, requiresFeature: "manageInventory", requiresModule: "INVENTORY" },
      { label: "Điều chỉnh tồn kho", path: "/inventory/adjustments", module: "inventory", icon: SlidersHorizontal, requiresFeature: "manageInventory", requiresModule: "INVENTORY" },
    ],
  },
  {
    label: "Tiêm chủng",
    items: [
      { label: "Đặt lịch tiêm", path: "/vaccination/booking", module: "vaccination", icon: CalendarPlus, requiresModule: "VACCINATION" },
      { label: "Khám & Tiêm", path: "/vaccination/workspace", module: "vaccination", icon: Syringe, requiresModule: "VACCINATION" },
      { label: "Bảng theo dõi", path: "/vaccination/observation", module: "vaccination", icon: Timer, requiresModule: "VACCINATION" },
      { label: "Danh mục & Phác đồ", path: "/vaccination/catalog", module: "vaccination", icon: Dna, requiresModule: "VACCINATION" },
      { label: "Sổ tiêm & Thu hồi lô", path: "/vaccination/records", module: "vaccination", icon: ClipboardList, requiresModule: "VACCINATION" },
    ],
  },
  {
    label: "Tài chính",
    items: [
      { label: "Hoá đơn", path: "/invoices", module: "invoices", icon: Receipt, requiresModule: "BILLING" },
      { label: "Thu chi", path: "/payments", module: "invoices", icon: Banknote, requiresModule: "BILLING" },
      { label: "Công nợ", path: "/receivables", module: "invoices", icon: Banknote, requiresModule: "BILLING" },
      // Tự động hoá giao lại việc cho con người: tiền đã về mà đoán là sai (#18998).
      { label: "Tiền về chưa khớp", path: "/bank-transfers", module: "invoices", icon: Banknote, requiresModule: "BILLING" },
      // Nghĩa vụ, không phải lịch sử: còn ở đây là tiền chưa về tay bệnh nhân (#18998).
      { label: "Hoàn tiền bệnh nhân", path: "/refunds", module: "invoices", icon: Banknote, requiresModule: "BILLING" },
      { label: "Mã giảm giá", path: "/coupons", module: "invoices", icon: Percent, requiresModule: "BILLING" },
    ],
  },
  {
    label: "Báo cáo",
    items: [
      // #19850 — mỗi mục mang đúng quyền mà endpoint của nó kiểm (ReportsController): người không
      // có quyền không còn thấy mục rồi gặp lỗi chung chung. "Nâng cao" đọc cả doanh thu theo chi
      // nhánh (tài chính) và tỷ lệ quay lại (vận hành): mục mở với quyền vận hành, còn bảng doanh
      // thu theo chi nhánh trong trang chỉ hiện khi có quyền tài chính.
      // #19860 — bác sĩ (report.self.read) cũng vào được: server tự buộc số của chính họ.
      { label: "Tổng quan vận hành", path: "/reports/operations", module: "reports", icon: Activity, requiresModule: "REPORTS", requiresPermission: ["report.operational.read", "report.self.read"] },
      { label: "Doanh thu / thực thu", path: "/reports/revenue", module: "reports", icon: TrendingUp, requiresModule: "REPORTS", requiresPermission: "report.financial.read" },
      { label: "Bệnh nhân", path: "/reports/patients", module: "reports", icon: Users, requiresModule: "REPORTS", requiresPermission: "report.operational.read" },
      { label: "Top dịch vụ", path: "/reports/top-services", module: "reports", icon: Star, requiresModule: "REPORTS", requiresPermission: "report.operational.read" },
      { label: "Top thuốc", path: "/reports/top-medicines", module: "reports", icon: Pill, requiresModule: "REPORTS", requiresPermission: "report.operational.read" },
      // Đánh giá không chạy qua ReportsController: FeedbackController kiểm patient.read.
      { label: "Đánh giá", path: "/feedback", module: "reports", icon: Star, requiresPermission: "patient.read" },
      { label: "Nâng cao", path: "/reports/advanced", module: "reports", icon: Microscope, requiresModule: "REPORTS", requiresPermission: "report.operational.read" },
    ],
  },
  {
    // ADR-0041: Website và Zalo Mini App là hai kênh song song của cùng một
    // phòng khám, nên chúng đứng chung một nhóm chứ không phải Mini App nằm
    // dưới đề mục "Website". Native App sẽ vào đây khi có.
    label: "Kênh số",
    items: [
      { label: "Website", path: "/website", module: "website", icon: Globe },
      { label: "Zalo Mini App", path: "/miniapp", module: "website", icon: Smartphone },
    ],
  },
  {
    label: "Cài đặt",
    defaultCollapsed: true,
    // Mọi nhóm Cài đặt (settings-nav.ts là nguồn duy nhất — thêm nhóm ở đó là tự có mục ở đây).
    // Mỗi mục dẫn tới route điểm đến của nhóm và mở hub Cài đặt; cột giữa của hub liệt kê các
    // mục của nhóm đó. Tô đậm theo nhóm, không theo đường dẫn chính xác.
    items: settingsGroups.map((g) => ({
      label: g.label,
      path: g.path,
      module: "settings",
      icon: g.icon,
      settingsGroup: g.label,
    })),
  },
];

export function breadcrumbFor(path: string): string[] {
  const trail: string[] = ["Addy HIS"];

  // "Me" routes — not in sidebar, but breadcrumb labels needed
  if (path.startsWith("/me/profile")) return [...trail, "Tài khoản", "Hồ sơ"];
  if (path.startsWith("/me/security")) return [...trail, "Tài khoản", "Bảo mật"];
  if (path.startsWith("/me/preferences")) return [...trail, "Tài khoản", "Tuỳ chọn"];

  // Settings routes: "Cài đặt › <category> › <item>" via the settings taxonomy.
  if (path.startsWith("/settings")) {
    const group = activeSettingsGroup(path);
    const item = group?.items.find(
      (it) => path === it.path || path.startsWith(it.path + "/")
    );
    trail.push("Cài đặt");
    if (group) trail.push(group.label);
    if (item) trail.push(item.label);
    return trail;
  }

  for (const group of navGroups) {
    for (const item of group.items) {
      if (path.startsWith(item.path)) {
        trail.push(group.label, item.label);
        return trail;
      }
    }
  }
  trail.push("Trang");
  return trail;
}
